import { useState } from "react";
import { presentableCarrierText } from "../../lib/carrier-brand";
import StepHeader from "../../components/StepHeader";
import type { RateQuote } from "@shared/types";

interface Props {
  quote: RateQuote;
  onPay: (confirmedHighValue: boolean) => void;
  onBack: () => void;
  paying: boolean;
  requiresConfirmation: boolean;
}

// Step 9 — payment. Most carriers here go through Stripe (the app operator
// purchases the actual carrier label once payment clears — see
// functions/lib/fulfil-shipment.ts). Parcel2Go is the exception: its own
// hosted checkout lets the sender pay Parcel2Go directly — confirmed live
// 2026-08-23 (see docs/parcel2go-payment-validation.md §D) that an
// anonymous viewer of that link never sees or can spend the app's own
// account balance. `isProviderHostedCheckout` switches the copy/button
// accordingly; the backend (checkout.ts) is what actually decides which
// payment mode runs, based on the selected provider's declared
// capabilities, not anything sent from here.
//
// `requiresConfirmation` is set when the backend's checkout endpoint
// returned REQUIRES_CONFIRMATION — a spending safeguard for unusually
// expensive shipments (see functions/lib/spending-guard.ts) — and shows an
// explicit "are you sure?" step rather than silently charging more.
export default function PaymentStep({ quote, onPay, onBack, paying, requiresConfirmation }: Props) {
  const [acknowledged, setAcknowledged] = useState(false);
  // Parcel2Go is currently the only provider with a hosted checkout — see
  // the file header above for why this is a plain id check rather than a
  // capability flag threaded onto RateQuote.
  const isProviderHostedCheckout = quote.providerId === "parcel2go";

  return (
    <div className="stack fade-in-up">
      <StepHeader title="Payment" onBack={onBack} disabled={paying} />
      <p style={{ color: "var(--color-text-muted)", margin: 0 }}>
        {isProviderHostedCheckout
          ? "You'll pay Parcel2Go directly on their own page — your card details are never handled by this site."
          : "You'll pay securely on Stripe's payment page, then come straight back here."}
      </p>

      <div className="card stack-sm">
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>
            {presentableCarrierText(quote.carrier, quote.carrierDisplayName)} — {presentableCarrierText(quote.carrier, quote.serviceDisplayName)}
          </span>
          <strong>£{quote.priceGbp.toFixed(2)}</strong>
        </div>
        <span className="field-help">{quote.estimatedDeliveryDays}</span>
        {quote.isMock && (
          <span className="tag" style={{ background: "var(--color-warn-border)", width: "fit-content" }}>
            TEST DATA — no real charge
          </span>
        )}
      </div>

      {requiresConfirmation && (
        <div className="banner banner-warn" role="alert">
          <div className="stack-sm">
            <strong>This is a higher-cost shipment — please double-check before paying.</strong>
            <label style={{ display: "flex", gap: "var(--space-2)", alignItems: "flex-start" }}>
              <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} style={{ marginTop: 4 }} />
              <span>Yes, I want to pay £{quote.priceGbp.toFixed(2)} for this.</span>
            </label>
          </div>
        </div>
      )}

      <button
        className="btn btn-primary"
        onClick={() => onPay(acknowledged)}
        disabled={paying || (requiresConfirmation && !acknowledged)}
      >
        {paying
          ? "Redirecting to payment…"
          : isProviderHostedCheckout
            ? `Continue to Parcel2Go to pay £${quote.priceGbp.toFixed(2)}`
            : `Pay £${quote.priceGbp.toFixed(2)}`}
      </button>
    </div>
  );
}
